import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { TimeInput } from '@/components/ui/TimeInput';
import { TYPE } from '@/lib/typography';
import { formatHourPeriods } from '@/lib/clinicHours';
import {
  resolvePractitionerDayHours,
  type MemberScheduleException,
} from '@/lib/practitionerHours';
import { WORKING_HOURS_DAY_KEYS } from '@/utils/appointmentCalendarDates';
import { formatAppointmentDateHeading } from '@/utils/appointmentTimeFormat';
import type { WorkingHours } from '@/hooks/useClinicUsers';
import {
  useAddMemberException,
  useDeleteMemberException,
  useUpdateMemberException,
} from '@/hooks/useClinicUsers';
import { useCountAppointmentsOnDate } from '@/hooks/useClinicHoursExceptions';

function singleDayOverride(
  exceptions: MemberScheduleException[],
  membershipId: string,
  isoDate: string,
): MemberScheduleException | null {
  return (
    exceptions.find(
      (row) =>
        row.membership_id === membershipId &&
        row.starts_on === isoDate &&
        row.ends_on === isoDate &&
        (row.exception_type === 'custom_hours' ||
          (row.exception_type === 'unavailable' && !row.starts_time && !row.ends_time)),
    ) ?? null
  );
}

export function PractitionerDayHoursModal({
  clinicId,
  date,
  isoDate,
  practitionerName,
  membershipId,
  workingHours,
  exceptions,
  canEdit,
  onClose,
}: {
  clinicId: string;
  date: Date;
  isoDate: string;
  practitionerName: string;
  membershipId: string;
  workingHours: WorkingHours;
  exceptions: MemberScheduleException[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const addException = useAddMemberException();
  const updateException = useUpdateMemberException();
  const deleteException = useDeleteMemberException();
  const appointmentCount = useCountAppointmentsOnDate(clinicId, isoDate);

  const weekly = workingHours[WORKING_HOURS_DAY_KEYS[date.getDay()]];
  const resolved = resolvePractitionerDayHours({
    workingHours,
    exceptions,
    membershipId,
    date,
  });
  const existing = singleDayOverride(exceptions, membershipId, isoDate);

  const [mode, setMode] = useState<'weekly' | 'closed' | 'custom'>(
    resolved.source === 'weekly' ? 'weekly' : resolved.enabled ? 'custom' : 'closed',
  );
  const [start, setStart] = useState(resolved.periods[0]?.start ?? weekly?.start ?? '09:00');
  const [end, setEnd] = useState(resolved.periods[0]?.end ?? weekly?.end ?? '17:00');

  useEffect(() => {
    const next = resolvePractitionerDayHours({
      workingHours,
      exceptions,
      membershipId,
      date,
    });
    setMode(next.source === 'weekly' ? 'weekly' : next.enabled ? 'custom' : 'closed');
    setStart(next.periods[0]?.start ?? weekly?.start ?? '09:00');
    setEnd(next.periods[0]?.end ?? weekly?.end ?? '17:00');
  }, [isoDate, exceptions, membershipId, workingHours, date, weekly?.start, weekly?.end]);

  const locale = i18n.language === 'da' ? 'da-DK' : 'en-US';
  const dateLabel = useMemo(() => formatAppointmentDateHeading(date, locale), [date, locale]);

  const weeklyLabel = weekly?.enabled
    ? formatHourPeriods([{ start: weekly.start, end: weekly.end }])
    : t('appointments.practitioner_hours.closed');
  const existingAppointments = appointmentCount.data ?? 0;
  const showCloseWarning = mode === 'closed' && existingAppointments > 0;
  const pending = addException.isPending || updateException.isPending || deleteException.isPending;

  async function save() {
    try {
      if (mode === 'weekly') {
        if (existing) {
          await deleteException.mutateAsync({
            exception_id: existing.id,
            membership_id: membershipId,
          });
        }
        toast.success(t('appointments.practitioner_hours.restored_toast'));
        onClose();
        return;
      }
      if (mode === 'closed') {
        if (
          showCloseWarning &&
          !window.confirm(
            t('appointments.practitioner_hours.close_with_appointments_confirm', {
              count: existingAppointments,
            }),
          )
        ) {
          return;
        }
        if (existing) {
          await updateException.mutateAsync({
            exception_id: existing.id,
            membership_id: membershipId,
            patch: {
              exception_type: 'unavailable',
              starts_on: isoDate,
              ends_on: isoDate,
              clear_times: true,
            },
          });
        } else {
          await addException.mutateAsync({
            membership_id: membershipId,
            exception_type: 'unavailable',
            starts_on: isoDate,
            ends_on: isoDate,
            starts_time: null,
            ends_time: null,
          });
        }
        toast.success(t('appointments.practitioner_hours.closed_toast'));
        onClose();
        return;
      }
      if (start >= end) {
        toast.error(t('appointments.practitioner_hours.invalid_hours'));
        return;
      }
      if (existing) {
        await updateException.mutateAsync({
          exception_id: existing.id,
          membership_id: membershipId,
          patch: {
            exception_type: 'custom_hours',
            starts_on: isoDate,
            ends_on: isoDate,
            starts_time: start,
            ends_time: end,
          },
        });
      } else {
        await addException.mutateAsync({
          membership_id: membershipId,
          exception_type: 'custom_hours',
          starts_on: isoDate,
          ends_on: isoDate,
          starts_time: start,
          ends_time: end,
        });
      }
      toast.success(t('appointments.practitioner_hours.saved_toast'));
      onClose();
    } catch (error) {
      console.error('practitioner hours exception failed', error);
      toast.error(t('appointments.practitioner_hours.save_error'));
    }
  }

  return (
    <Modal title={t('appointments.practitioner_hours.title', { name: practitionerName })} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <div className={`${TYPE.section} normal-case text-ds-text`}>{dateLabel}</div>
          <p className={`${TYPE.small} mt-1 text-ds-muted`}>
            {t('appointments.practitioner_hours.subtitle', { name: practitionerName })}
          </p>
        </div>

        <div className="rounded-lg bg-ds-sunk p-3">
          <div className={`${TYPE.small} text-ds-muted`}>
            {t('appointments.practitioner_hours.normal_hours')}
          </div>
          <div className={`${TYPE.body} font-medium text-ds-text`}>{weeklyLabel}</div>
        </div>

        {canEdit ? (
          <fieldset className="space-y-2">
            <legend className={`${TYPE.label} text-ds-text`}>
              {t('appointments.practitioner_hours.this_date')}
            </legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="practitioner-hours-mode"
                checked={mode === 'weekly'}
                onChange={() => setMode('weekly')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>{t('appointments.practitioner_hours.use_normal')}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="practitioner-hours-mode"
                checked={mode === 'closed'}
                onChange={() => setMode('closed')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>{t('appointments.practitioner_hours.close_day')}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="practitioner-hours-mode"
                checked={mode === 'custom'}
                onChange={() => setMode('custom')}
                className="h-4 w-4 accent-ds-teal"
              />
              <span className={TYPE.body}>{t('appointments.practitioner_hours.change_hours')}</span>
            </label>
          </fieldset>
        ) : (
          <p className={`${TYPE.small} text-ds-muted`}>{t('appointments.practitioner_hours.read_only')}</p>
        )}

        {mode === 'custom' && canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <TimeInput value={start} onChange={setStart} />
            <span className={`${TYPE.small} text-ds-muted`}>{t('settings.hours.to')}</span>
            <TimeInput value={end} onChange={setEnd} />
          </div>
        )}

        {showCloseWarning && (
          <div className="flex gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint p-3 text-ds-amber-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className={TYPE.small}>
              {t('appointments.practitioner_hours.close_with_appointments', {
                count: existingAppointments,
              })}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            {t('common.cancel', 'Annuller')}
          </Button>
          {canEdit && (
            <Button type="button" onClick={() => void save()} disabled={pending}>
              {t('common.save', 'Gem')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
